// Shared Organization-Services portal styling — mirrors the Prism Admin Portal
// design language so CloudTrail, Security Hub and StackSets read as one console.
//
// The Admin portal hardcodes a fixed CloudKeeper palette, sets fontFamily inline
// (Bitter serif titles, Metropolis body), draws status as a pastel-triad chip
// (pastel bg / saturated fg / `${fg}20` border), wraps content in bordered
// `Paper elevation={0}` cards, and styles data tables with an `#F9FBFD` head +
// Metropolis-600 navy cells + a staggered row entrance. These helpers centralize
// exactly those conventions for the portal.
//
// This file is otherwise tokens-only, but it also carries the two shared
// *components* at the bottom — <ResourceTree> and <UtilizationBar>. They live
// here rather than in a capability folder because they are design language, not
// capability logic, and because the alternative (a second module that re-exports
// half of this one) is how a palette drifts. Both are PURE: no hooks, no state,
// no fetches, no effects. That is the mitigation for putting JSX in a file 80+
// modules import — a page that only wanted `CK.navy` cannot be broken by a
// component it never renders.
import {
  Box,
  IconButton,
  LinearProgress,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tooltip,
  Typography,
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';

// --- CloudKeeper palette (design tokens) ---
export const CK = {
  navy: '#253E66', // primary text / titles / table headers
  body: '#666666', // secondary body text
  muted: '#999999', // subtitles, captions, empty text
  disabled: '#BDBDBD',
  border: '#E5E5E5', // universal border / divider
  pageBg: '#F9FBFD', // page background / table head / inset panels
  surface: '#FFFFFF',
  hover: '#F0F4F8', // icon-button hover surface
  blue: '#4698D3',
  blueDark: '#3580B8',
  green: '#27AE60',
  amber: '#F39C12',
  red: '#E74C3C',
  redDark: '#C0392B',
  purple: '#8E44AD',
};

export const FONT_TITLE = 'Bitter, Georgia, serif';
export const FONT_BODY = 'Metropolis, sans-serif';

// A SYSTEM stack, not a third webfont — the portal already loads two, and a
// network request for table cells is not worth it. Used wherever a column has to
// be scanned character-by-character rather than read: CIDRs and IP addresses,
// `ipam-*` / `vpc-*` / `subnet-*` / `eni-*` ids, 12-digit account ids and ARNs.
export const FONT_MONO =
  'ui-monospace, SFMono-Regular, "SF Mono", Menlo, Consolas, "Liberation Mono", monospace';

// --- Status chips: pastel-triad (bg / fg / `${fg}20` border) ---
// Intents map onto the Admin portal's semantic triads.
const TRIAD = {
  success: { fg: '#27AE60', bg: '#F0F9F4' },
  warning: { fg: '#F39C12', bg: '#FEF9E7' },
  error: { fg: '#E74C3C', bg: '#FDF2F2' },
  info: { fg: '#4698D3', bg: '#EBF5FB' },
  purple: { fg: '#8E44AD', bg: '#F5EBF8' },
  neutral: { fg: '#999999', bg: '#F5F5F5' },
};

// statusChipSx returns the sx for a small pastel-triad status Chip.
// Usage: <Chip size="small" label={formatStatusLabel(x)} sx={statusChipSx('success')} />
export function statusChipSx(intent = 'neutral') {
  const t = TRIAD[intent] || TRIAD.neutral;
  return {
    height: 24,
    fontSize: '0.75rem',
    fontFamily: FONT_BODY,
    fontWeight: 600,
    backgroundColor: t.bg,
    color: t.fg,
    border: `1px solid ${t.fg}20`,
    '& .MuiChip-icon': { color: t.fg },
  };
}

// Map a MUI-style color name ('success'|'warning'|'error'|'info'|'default'|...)
// to a triad intent — lets existing `color={statusColor(x)}` maps be reused.
export const intentFromColor = (c) => ({
  success: 'success',
  warning: 'warning',
  error: 'error',
  info: 'info',
  primary: 'info',
  secondary: 'purple',
  default: 'neutral',
}[c] || 'neutral');

// --- Data table (Admin style) ---
export const tableHeadRowSx = { backgroundColor: CK.pageBg };

export const headCellSx = {
  fontFamily: FONT_BODY,
  fontWeight: 600,
  fontSize: '0.875rem',
  color: CK.navy,
  borderBottom: `1px solid ${CK.border}`,
  whiteSpace: 'nowrap',
};

// rowSx(index) → staggered fade-in entrance + hover + last-row border drop.
// The per-row delay is capped so large/paginated tables (e.g. Findings, which
// loads 50 rows at a time and can grow to hundreds) never leave late rows
// invisible for seconds or keep the compositor busy — the first ~12 rows stagger
// in, the rest share the same small delay.
export const rowSx = (i = 0) => ({
  animation: `portalFadeInRow 0.3s ease-out ${Math.min(i, 12) * 0.03}s both`,
  '@keyframes portalFadeInRow': {
    from: { opacity: 0, transform: 'translateY(8px)' },
    to: { opacity: 1, transform: 'translateY(0)' },
  },
  '&:hover': { backgroundColor: CK.pageBg },
  '&:last-child td': { borderBottom: 'none' },
});

export const bodyCellSx = {
  fontFamily: FONT_BODY,
  fontSize: '0.875rem',
  color: CK.navy,
  borderBottom: `1px solid ${CK.border}`,
};

// Body cell for an address, a CIDR or an opaque id. Without tabular alignment
// `10.100.0.0/16` and `10.10.0.0/16` are indistinguishable in a scanned column,
// which is the single most common read on an addressing screen.
//
// `whiteSpace: 'nowrap'` deliberately overrides the `wordBreak: 'break-word'`
// that sibling pages apply to ids: a CIDR wrapped across two lines is
// unreadable. The column scrolls inside its TableContainer instead.
//
// It does NOT sort. CIDRs do not order lexicographically — as strings
// `"10.9.0.0/16" > "10.10.0.0/16"` — so ordering is the server's manufactured
// sort key, never anything this cell derives from what it is handed.
export const cidrCellSx = {
  ...bodyCellSx,
  fontFamily: FONT_MONO,
  fontVariantNumeric: 'tabular-nums',
  whiteSpace: 'nowrap',
  letterSpacing: '-0.01em',
};

// --- Containers ---
// Bordered section card (padded).
export const sectionCardSx = {
  backgroundColor: CK.surface,
  border: `1px solid ${CK.border}`,
  borderRadius: '8px',
  p: 3,
};

// List card (unpadded; clips a table to the rounded corners).
export const listCardSx = {
  backgroundColor: CK.surface,
  border: `1px solid ${CK.border}`,
  borderRadius: '8px',
  overflow: 'hidden',
};

// Inset highlighted panel inside a card.
export const insetPanelSx = {
  backgroundColor: CK.pageBg,
  border: `1px solid ${CK.border}`,
  borderRadius: '8px',
  p: 2.5,
};

// Section heading (Bitter, navy). Spread onto a <Typography>.
export const sectionHeadingSx = {
  fontFamily: FONT_TITLE,
  fontWeight: 600,
  fontSize: '1.1rem',
  color: CK.navy,
};

// Muted empty-state text.
export const emptyTextSx = {
  fontFamily: FONT_BODY,
  color: CK.muted,
};

// --- Layout rhythm ---
// The Admin portal stacks section cards 24px apart under a header card that
// also sits 24px above the first section. Portal pages must use the same gap
// so the two consoles scroll at the same rhythm.
export const SECTION_GAP = 24;

// Spread onto the motion.div (or Box) wrapping a non-first section card.
export const sectionGapSx = { mt: 3 };

// --- Stat tiles ---
// Tiles in a Grid row must be equal height with their footer pinned to the
// bottom, otherwise a row of cards with differing body lengths reads as ragged.
// Usage: <Paper sx={statTileSx}> …body… <Box sx={statTileFooterSx}>…</Box></Paper>
export const statTileSx = {
  ...sectionCardSx,
  height: '100%',
  display: 'flex',
  flexDirection: 'column',
};

// Pins a tile's action/footer row to the bottom of an equal-height tile.
export const statTileFooterSx = { mt: 'auto', pt: 2 };

// --- Rows that must survive a narrow viewport ---
// A left/right split (title on the left, actions on the right) that wraps
// instead of crushing its children.
export const splitRowSx = {
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  gap: 2,
  flexWrap: 'wrap',
};

// A group of buttons/filters that wraps rather than overflowing.
export const actionRowSx = {
  display: 'flex',
  alignItems: 'center',
  gap: 1.5,
  flexWrap: 'wrap',
};

// --- Dialogs ---
// Every portal dialog is width-stable and rounded like the Admin console's.
// Usage: <Dialog open={x} onClose={y} {...dialogProps}>
export const dialogProps = {
  fullWidth: true,
  maxWidth: 'sm',
  PaperProps: {
    sx: {
      borderRadius: '12px',
      border: `1px solid ${CK.border}`,
      backgroundColor: CK.surface,
    },
  },
};

// Dialog title band — pastel fill + Bitter title, matching the Admin console.
export const dialogTitleSx = {
  backgroundColor: CK.pageBg,
  borderBottom: `1px solid ${CK.border}`,
  fontFamily: FONT_TITLE,
  fontWeight: 600,
  fontSize: '1.25rem',
  color: CK.navy,
  py: 2,
  px: 3,
};

// Entrance for a Grid tile, as a plain inline style on a plain <div>.
//
// Deliberately NOT a motion variant. Tiles sit inside MUI <Grid container>/
// <Grid>, and a variant label does not propagate through those to a nested
// motion.div — the tile then keeps the library's inline `opacity: 0` forever
// and renders correctly sized but completely invisible. Pure CSS keyframes
// (`portalTileIn`, defined globally in App.css) cannot fail that way.
//
// The cascade comes from the DURATION, not a delay, and the keyframes move
// ONLY the transform — opacity is never animated. A tile is therefore fully
// opaque at every instant, including frame zero, so no interrupted, delayed or
// never-started animation can leave it blank. That property matters more here
// than a fade: this content went missing in production twice.
export const tileEntranceStyle = (index = 0) => ({
  height: '100%',
  animation: `portalTileIn ${(0.3 + Math.min(index, 12) * 0.08).toFixed(2)}s ease-out both`,
});

// ===========================================================================
// <UtilizationBar> — "how full is this address space"
// ===========================================================================
//
// NOT OperationProgress. That is a *job* bar: `mb: 2` is baked into its root, the
// fill is hardcoded to CK.blue, it has no threshold concept, and — fatally —
// `percent == null` means INDETERMINATE there. Here a null value means "AWS
// cannot calculate this", and an animated indeterminate bar promises a number
// that is never going to arrive.

// Default bands. Below 70 % a space is fine, 70–90 % needs planning, 90 %+ is an
// imminent exhaustion. Passed as a PROP, not read as a constant, because 90 % of
// a /16 is fine and 90 % of a /28 is an outage — a subnet view wants tighter
// bands (`{ amber: 80, red: 95 }`) than a pool view.
export const UTILIZATION_THRESHOLDS = { amber: 70, red: 90 };

// The default explanation for an unmeasurable value. AWS does not compute
// utilization for IPv6 space at all — the field comes back absent, forever.
export const UTILIZATION_UNAVAILABLE_TEXT =
  'AWS does not calculate IP usage for IPv6 address space.';

// utilizationIntent maps a 0..100 utilization onto a statusChipSx intent, so a
// 92 %-full pool's bar and its "Nearly full" chip resolve to the same TRIAD
// entry and are the same red pixel-for-pixel — they cannot drift, because
// neither one holds a hex.
//
// Returns `null`, never an intent, when there is no number to band. An absent
// utilization is NOT 0 %: `null` means "not computable" and `0` means
// "genuinely empty", and both occur in real data. Folding the first into
// `success` would paint every IPv6 prefix healthy green — a false all-clear on
// the one question the screen exists to answer. Callers that pass the result
// straight through still degrade honestly: `statusChipSx(null)` resolves to the
// neutral grey chip, not to a green one.
export function utilizationIntent(value, thresholds) {
  if (value === null || value === undefined || value === '') return null;
  const v = Number(value);
  if (!Number.isFinite(v)) return null;
  const amber = Number.isFinite(thresholds?.amber)
    ? thresholds.amber
    : UTILIZATION_THRESHOLDS.amber;
  const red = Number.isFinite(thresholds?.red) ? thresholds.red : UTILIZATION_THRESHOLDS.red;
  if (v >= red) return 'error';
  if (v >= amber) return 'warning';
  return 'success';
}

// A pool holding one address out of 65 536 is not an empty pool, so it must not
// read "0 %" — that is the same false all-clear in a second disguise.
const formatUtilizationPercent = (v) => {
  if (v > 0 && v < 1) return '<1%';
  if (v > 99 && v < 100) return '>99%';
  return `${Math.round(v)}%`;
};

// Props:
//   value       0..100, or null/undefined when AWS did not compute it
//   thresholds  { amber, red } — defaults to UTILIZATION_THRESHOLDS
//   label       optional caption rendered above the track
//   showValue   render the numeric readout (default true)
//   unavailableTooltip  overrides the null-state explanation
//   width, sx   layout only — NO margin is baked into the root
export function UtilizationBar({
  value,
  thresholds,
  label,
  showValue = true,
  unavailableTooltip = UTILIZATION_UNAVAILABLE_TEXT,
  width,
  sx,
}) {
  const intent = utilizationIntent(value, thresholds);

  // No number → NO BAR. Not an empty track, not an indeterminate sweep: a muted
  // dash that carries its reason in a tooltip. A track drawn at zero would be
  // indistinguishable from a genuinely empty space.
  if (intent === null) {
    return (
      <Tooltip title={unavailableTooltip}>
        <Box
          component="span"
          aria-label={`Not measured. ${unavailableTooltip}`}
          sx={{
            display: 'inline-flex',
            alignItems: 'center',
            fontFamily: FONT_BODY,
            fontSize: '0.8rem',
            color: CK.muted,
            cursor: 'help',
            ...sx,
          }}
        >
          &mdash;
        </Box>
      </Tooltip>
    );
  }

  // AWS can report over 100 for an over-provisioned space, and an unclamped
  // LinearProgress overflows its own track.
  const pct = Math.max(0, Math.min(100, Number(value)));
  const text = formatUtilizationPercent(pct);
  const fill = (TRIAD[intent] || TRIAD.neutral).fg;

  return (
    <Box sx={{ minWidth: 0, width, ...sx }}>
      {(label || showValue) && (
        <Box
          sx={{
            display: 'flex',
            alignItems: 'baseline',
            justifyContent: label ? 'space-between' : 'flex-end',
            gap: 1,
            mb: 0.5,
          }}
        >
          {label && (
            <Typography
              sx={{ fontFamily: FONT_BODY, fontSize: '0.75rem', color: CK.body, minWidth: 0 }}
            >
              {label}
            </Typography>
          )}
          {showValue && (
            <Typography
              sx={{
                fontFamily: FONT_BODY,
                fontSize: '0.75rem',
                fontWeight: 600,
                color: fill,
                flexShrink: 0,
              }}
            >
              {text}
            </Typography>
          )}
        </Box>
      )}
      <LinearProgress
        variant="determinate"
        value={pct}
        aria-label={`${text} allocated`}
        sx={{
          height: 6,
          borderRadius: 3,
          backgroundColor: CK.border,
          '& .MuiLinearProgress-bar': { borderRadius: 3, backgroundColor: fill },
        }}
      />
    </Box>
  );
}

// ===========================================================================
// <ResourceTree> — a hierarchy that is still a portal table
// ===========================================================================
//
// Every other table in the portal is flat, and the two near-misses are not
// substitutes: PortalSidebar's <Collapse> is a one-level navigation primitive
// with no columns, OrphanScanPage's second row is a colSpan *detail* row that
// neither indents nor recurses, and OrgTreeCheckbox is an uncontrolled Box/
// Collapse picker, also columnless.
//
// One real <Table>, never nested ones: nested tables break column alignment
// across depths, which is fatal on the page whose entire value is comparing
// utilization down a hierarchy.

const TREE_PAD_BASE = 2; // spacing units of left padding at depth 0
const TREE_INDENT = 2.5; // spacing units added per level
const TREE_CARET_SLOT = 28; // px — caret button and leaf spacer are the same width,
// so sibling labels share one left edge whether or not they have children
const TREE_DEPTH_SCAN_CAP = 50; // only bounds the "N deeper levels not shown" count

const treeCaptionCellSx = {
  ...bodyCellSx,
  color: CK.muted,
  fontSize: '0.75rem',
  fontStyle: 'italic',
  py: 0.75,
};

const defaultGetChildren = (node) => (node && node.children) || [];

const expandedTest = (expanded) => {
  if (expanded instanceof Set) return (id) => expanded.has(id);
  if (Array.isArray(expanded)) return (id) => expanded.includes(id);
  if (expanded && typeof expanded === 'object') return (id) => Boolean(expanded[id]);
  return () => false;
};

// How many levels sit below `node`. Breadth-first and capped, so a malformed
// (cyclic) feed cannot hang the tab; only ever used to word a caption.
function remainingDepth(node, getChildren) {
  let levels = 0;
  let frontier = getChildren(node) || [];
  while (frontier.length > 0 && levels < TREE_DEPTH_SCAN_CAP) {
    levels += 1;
    const next = [];
    frontier.forEach((child) => {
      (getChildren(child) || []).forEach((grandchild) => next.push(grandchild));
    });
    frontier = next;
  }
  return levels >= TREE_DEPTH_SCAN_CAP ? `${TREE_DEPTH_SCAN_CAP}+` : levels;
}

// Nesting is a computed FLAT list: nodes + expanded → [{ node, depth }]. Rows
// are then plain siblings in one <TableBody>, which is what keeps every column
// on one grid regardless of depth.
function flattenTree(nodes, getId, getChildren, isExpanded, maxDepth) {
  const rows = [];
  const walk = (list, depth) => {
    (list || []).forEach((node) => {
      const id = getId(node);
      const children = getChildren(node) || [];
      const atCap = depth >= maxDepth;
      const hasChildren = children.length > 0 && !atCap;
      const open = hasChildren && isExpanded(id);
      rows.push({ kind: 'node', key: String(id), node, id, depth, hasChildren, expanded: open });
      if (children.length > 0 && atCap) {
        // maxDepth is honest, not silent: the subtree is declared, not dropped.
        rows.push({
          kind: 'truncated',
          key: `${id}::truncated`,
          depth: depth + 1,
          hidden: remainingDepth(node, getChildren),
        });
      } else if (open) {
        walk(children, depth + 1);
      }
    });
  };
  walk(nodes, 0);
  return rows;
}

// buildResourceTree — a flat parent-pointer list → the `nodes` array
// <ResourceTree> renders, WITHOUT ever dropping a row.
//
// A silently missing row is the worst failure a hierarchy view has: it is
// indistinguishable from "you do not have that resource". So an item whose
// parent id does not resolve — absent from the list, or reachable only through a
// cycle — is attached to `orphanRoot` rather than discarded. The caller owns what
// that root LOOKS like (its label, its warning chip, its tooltip); this owns only
// the guarantee that the row is still on the screen.
//
// `getParentId` returns null/undefined for a genuine root. A caller that has a
// third case — an item it can read but cannot place — maps that case to the
// orphan root's own id; if it forgets, the unresolved-parent branch catches the
// item anyway.
//
// Nodes are shallow-cloned with a fresh `children` array, so the caller's own
// objects are never mutated.
export function buildResourceTree(items, options = {}) {
  const { getId, getParentId, orphanRoot } = options;
  const list = Array.isArray(items) ? items : [];
  const byId = new Map();
  list.forEach((item) => {
    const id = getId(item);
    if (!byId.has(id)) byId.set(id, { ...item, children: [] });
  });

  const roots = [];
  const orphans = [];
  list.forEach((item) => {
    const id = getId(item);
    const node = byId.get(id);
    const parentId = getParentId ? getParentId(item) : null;
    if (parentId === null || parentId === undefined) {
      roots.push(node);
      return;
    }
    const parent = parentId === id ? null : byId.get(parentId);
    if (parent) parent.children.push(node);
    else orphans.push(node);
  });

  // Anything reachable only through a parent cycle is attached nowhere and would
  // otherwise vanish. Sweep for it rather than trusting the feed to be acyclic.
  const reached = new Set();
  const stack = [...roots, ...orphans];
  while (stack.length > 0) {
    const node = stack.pop();
    const id = getId(node);
    if (reached.has(id)) continue;
    reached.add(id);
    node.children.forEach((child) => stack.push(child));
  }
  byId.forEach((node, id) => {
    if (!reached.has(id)) orphans.push(node);
  });

  if (orphans.length === 0) return roots;
  // No orphan root supplied: show them at the top level rather than nowhere.
  if (!orphanRoot) return [...roots, ...orphans];
  const root = byId.get(getId(orphanRoot)) || { ...orphanRoot, children: [] };
  orphans.forEach((node) => {
    if (!root.children.includes(node)) root.children.push(node);
  });
  return roots.includes(root) ? roots : [...roots, root];
}

// Props:
//   nodes       root nodes; children read through getChildren
//   columns     [{ key, label, align, sx, headSx, width, render(node, row) }]
//               — column 0 is the tree column and carries the caret + indent
//   getId       node => stable id. Rows are keyed by it, NEVER by index:
//               rowSx(i) is a CSS keyframe entrance, so an index key remounts
//               every row below a toggle and restarts its animation — the whole
//               table flickers on every click.
//   getChildren node => child array (default `node.children`)
//   getLabel    node => string, for the caret's aria-label (default getId)
//   expanded    CONTROLLED — a Set, an array of ids, or an id→bool map. Being
//               controlled is what lets a page seed it from useSearchParams and
//               deep-link `?expand=<id>` to a branch.
//   onToggle    (id, nextExpanded, node) => void
//   maxDepth    deepest rendered depth index (default 10)
//   minWidth    the table's minWidth — indentation eats horizontal room, and a
//               6-deep row must not crush the columns on the right
export function ResourceTree({
  nodes,
  columns,
  getId,
  getChildren = defaultGetChildren,
  getLabel,
  expanded,
  onToggle,
  maxDepth = 10,
  minWidth = 720,
  size = 'small',
  empty = null,
  containerSx,
  tableSx,
}) {
  const cols = Array.isArray(columns) ? columns : [];
  const span = Math.max(cols.length, 1);
  const rows = flattenTree(nodes, getId, getChildren, expandedTest(expanded), maxDepth);
  const labelOf = (node) => String((getLabel ? getLabel(node) : getId(node)) ?? '');

  return (
    <TableContainer sx={{ overflowX: 'auto', ...containerSx }}>
      <Table size={size} sx={{ minWidth, ...tableSx }}>
        <TableHead>
          <TableRow sx={tableHeadRowSx}>
            {cols.map((col, ci) => (
              <TableCell
                key={col.key ?? ci}
                align={col.align || 'left'}
                sx={{ ...headCellSx, ...(col.width ? { width: col.width } : null), ...col.headSx }}
              >
                {col.label}
              </TableCell>
            ))}
          </TableRow>
        </TableHead>
        <TableBody>
          {rows.map((row, i) =>
            row.kind === 'truncated' ? (
              <TableRow key={row.key} sx={{ '&:hover': { backgroundColor: 'transparent' } }}>
                <TableCell
                  colSpan={span}
                  sx={{ ...treeCaptionCellSx, pl: TREE_PAD_BASE + row.depth * TREE_INDENT }}
                >
                  {`${row.hidden} deeper ${row.hidden === 1 ? 'level' : 'levels'} not shown`}
                </TableCell>
              </TableRow>
            ) : (
              <TableRow
                key={row.key}
                role="row"
                aria-level={row.depth + 1}
                aria-expanded={row.hasChildren ? row.expanded : undefined}
                hover
                sx={rowSx(i)}
              >
                {cols.map((col, ci) => {
                  const content =
                    typeof col.render === 'function'
                      ? col.render(row.node, row)
                      : row.node?.[col.key];
                  if (ci !== 0) {
                    return (
                      <TableCell
                        key={col.key ?? ci}
                        align={col.align || 'left'}
                        sx={{ ...bodyCellSx, ...col.sx }}
                      >
                        {content}
                      </TableCell>
                    );
                  }
                  return (
                    <TableCell
                      key={col.key ?? ci}
                      align={col.align || 'left'}
                      sx={{
                        ...bodyCellSx,
                        ...col.sx,
                        pl: TREE_PAD_BASE + row.depth * TREE_INDENT,
                      }}
                    >
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, minWidth: 0 }}>
                        {row.hasChildren ? (
                          <IconButton
                            size="small"
                            aria-label={`${row.expanded ? 'Collapse' : 'Expand'} ${labelOf(row.node)}`}
                            onClick={() => onToggle && onToggle(row.id, !row.expanded, row.node)}
                            sx={{
                              width: TREE_CARET_SLOT,
                              height: TREE_CARET_SLOT,
                              p: 0,
                              flexShrink: 0,
                              '&:hover': { backgroundColor: CK.hover },
                            }}
                          >
                            <ExpandMoreIcon
                              sx={{
                                fontSize: '1.1rem',
                                color: CK.body,
                                transform: row.expanded ? 'none' : 'rotate(-90deg)',
                                transition: 'transform 0.2s ease',
                              }}
                            />
                          </IconButton>
                        ) : (
                          <Box sx={{ width: TREE_CARET_SLOT, flexShrink: 0 }} />
                        )}
                        <Box sx={{ minWidth: 0 }}>{content}</Box>
                      </Box>
                    </TableCell>
                  );
                })}
              </TableRow>
            )
          )}
          {rows.length === 0 && empty && (
            <TableRow>
              <TableCell colSpan={span} align="center" sx={{ py: 4, borderBottom: 'none' }}>
                {empty}
              </TableCell>
            </TableRow>
          )}
        </TableBody>
      </Table>
    </TableContainer>
  );
}
